import dayjs from 'dayjs'
import { mapState } from 'vuex'

import { getUser } from '../services'
import { errorMessageGenerator } from '../validators'
import { calculatePaymentPercentage } from '@/helpers'

const ContractInfoMixin = {
  data () {
    return {
      contractManager: '',
      packageManager: '',
      projectController: ''
    }
  },
  computed: {
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    parts () {
      return this.contract.parts
        ? this.contract.parts.map(part => {
          const partPercentage = calculatePaymentPercentage(part)
          const adjustedBase = part.signedPriceCents + part.adjustedPriceCents
          const forecastedCombined = adjustedBase + part.forecastedPriceCents
          return {
            ...part,
            forecastedPriceCentsCombined: forecastedCombined,
            forecastedPricePercent: ((forecastedCombined * 100 / part.signedPriceCents) || 0).toFixed(2),
            adjustedPricePercent: ((adjustedBase * 100 / part.signedPriceCents) || 0).toFixed(2),
            currentPercent: partPercentage.toFixed(2)
          }
        })
        : []
    },
    forecastedPriceCents () {
      return this.adjustedPriceCents + this.contract.forecastedPriceCents
    },
    forecastedPriceCentsPerCurrency () {
      return Object.keys(this.contract.forecastedPriceCentsPerCurrency || {}).reduce((acc, key) => {
        const forecasted = this.contract.forecastedPriceCentsPerCurrency[key]
        const adjustedPerCurrency = (this.adjustedPriceCentsPerCurrency && this.adjustedPriceCentsPerCurrency[key]) ? this.adjustedPriceCentsPerCurrency[key] : (this.contract.signedPriceCentsPerCurrency[key] + this.contract.adjustedPriceCentsPerCurrency[key])
        return {
          ...acc,
          [key]: adjustedPerCurrency + forecasted
        }
      }, {})
    },
    adjustedPriceCents () {
      return this.signedPriceCents + this.contract.adjustedPriceCents
    },
    adjustedPriceCentsPerCurrency () {
      return Object.keys(this.contract.adjustedPriceCentsPerCurrency || {}).reduce((acc, key) => {
        const signed = this.contract.signedPriceCentsPerCurrency[key]
        const adjustedDelta = this.contract.adjustedPriceCentsPerCurrency[key]
        return {
          ...acc,
          [key]: signed + adjustedDelta
        }
      }, {})
    },
    forecastedPricePercent () {
      return Math.round((this.forecastedPriceCents / this.signedPriceCents) * 10000) / 100
    },
    adjustedPricePercent () {
      return Math.round((this.adjustedPriceCents / this.signedPriceCents) * 10000) / 100
    },
    beforeNegotiationsPriceCents () {
      return this.signedPriceCents + this.contract.beforeNegotiationsPriceCents
    },
    beforeNegotiationsPerCurrency () {
      return Object.keys(this.contract.beforeNegotiationsPriceCentsPerCurrency || {}).reduce((acc, key) => {
        return {
          ...acc,
          [key]: this.contract.signedPriceCentsPerCurrency[key] + this.contract.beforeNegotiationsPriceCentsPerCurrency[key]
        }
      }, {})
    },
    afterNegotiationsPriceCents () {
      return this.signedPriceCents + this.contract.afterNegotiationsPriceCents
    },
    afterNegotiationsPerCurrency () {
      return Object.keys(this.contract.afterNegotiationsPriceCentsPerCurrency || {}).reduce((acc, key) => {
        return {
          ...acc,
          [key]: this.contract.signedPriceCentsPerCurrency[key] + this.contract.afterNegotiationsPriceCentsPerCurrency[key]
        }
      }, {})
    },
    signedPriceCents () {
      return this.contract.signedPriceCents
    },
    currentPriceCents () {
      return this.contract.currentPriceCents
    },
    currentPriceCentsPerCurrency () {
      return this.contract.currentPriceCentsPerCurrency
    },
    projectDisplayName () {
      return this.contract.project
        ? this.contract.project.code
          ? `${this.contract.project.name} - ${this.contract.project.code}`
          : this.contract.project.name
        : ''
    },
    currentPercent () {
      const percentage = calculatePaymentPercentage(this.contract.parts)
      return percentage.toFixed(2)
    },
    adjustedDays () {
      return dayjs(this.contract.adjustedCompletionTime).diff(
        this.contract.completionTime,
        'day'
      )
    },
    purchaseOrderList () {
      const partPurchaseOrders = this.contract.parts ? this.contract.parts.map(i => i.purchaseOrder).filter(i => i) : []
      return this.contract.paymentMilestones
        ? this.contract.paymentMilestones.filter(i => !partPurchaseOrders.includes(i.purchaseOrder)).filter(i => !!i.purchaseOrder)
        : []
    },
    employer () {
      return this.contract.employer && this.contract.employer.companyName
        ? `${this.contract.employer.companyName}`
        : ''
    },
    employerAddress () {
      return this.contract.employer && this.contract.employer.street
        ? `${this.contract.employer.street} ${this.contract.employer.houseNumber} ${this.contract.employer.zipCode} ${this.contract.employer.city} ${this.contract.employer.state}`
        : ''
    },
    contractor () {
      return this.contract.contractor && this.contract.contractor.companyName
        ? `${this.contract.contractor.companyName} (${this.contract.contractor.sapVendorNumber || ''})`
        : ''
    },
    contractorAddress () {
      return this.contract.contractor && this.contract.contractor.street
        ? `${this.contract.contractor.street} ${this.contract.contractor.houseNumber} ${this.contract.contractor.zipCode} ${this.contract.contractor.city} ${this.contract.contractor.state}`
        : ''
    },
    savings () {
      // savings
      const diff = this.beforeNegotiationsPriceCents - this.afterNegotiationsPriceCents
      const percent = this.$options.filters.toFixed(
        (diff / (this.beforeNegotiationsPriceCents / 100))
      )

      return this.beforeNegotiationsPriceCents
        ? `${this.$options.filters.formatCurrency(diff)} (${percent}%)`
        : '0 (0%)'
    }
  },
  methods: {
    errorMessages (model, key) {
      return errorMessageGenerator(model, key)
    }
  },
  async mounted () {
    const contractManager = await getUser(this.contract.contractManager)
    this.contractManager = contractManager ? contractManager.displayName : this.contract.contractManager
    const packageManager = await getUser(this.contract.packageManager)
    this.packageManager = packageManager ? packageManager.displayName : this.contract.packageManager
    const projectController = await getUser(this.contract.projectController)
    this.projectController = projectController ? projectController.displayName : this.contract.projectController
  },
  watch: {
    async 'contract.packageManager' (v, p) {
      const packageManager = await getUser(v)
      this.packageManager = packageManager ? packageManager.displayName : this.contract.packageManager
    },
    async 'contract.projectController' (v, p) {
      const projectController = await getUser(v)
      this.projectController = projectController ? projectController.displayName : this.contract.projectController
    },
    async 'contract.contractManager' (v, p) {
      const contractManager = await getUser(v)
      this.contractManager = contractManager ? contractManager.displayName : this.contract.contractManager
    }
  }
}

export default ContractInfoMixin
